/**
 * UnifiedMentionSuggestion — `@` 통합 멘션 드롭다운
 *
 * 하나의 트리거(`@`)로 사용자(@user)와 위키 페이지(@page:)를 모두 노출.
 * 사용자 입력으로 양쪽을 동시 필터링하고, 선택 항목 종류에 따라 Mention 노드에
 * 다른 attrs.mentionType 을 저장한다.
 *
 * - kind='user' → mentionType='user', label=사용자 이름
 * - kind='page' → mentionType='wiki-page', label=페이지 제목
 *
 * Mention 노드의 attrs.mentionType 은 다음 위치에서 활용:
 *   - extractMentionedUserIds (MentionSuggestion.tsx) — user 만 카운트
 *   - extractLinkedPageIds (BoardService.ts)         — wiki-page 만 카운트
 */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
  useCallback,
  useRef,
} from 'react';
import { Trans, t } from '@lingui/macro';
import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';
import { cn } from '../../lib/utils';
import { FileText, Users } from 'lucide-react';
import { UserAvatar } from '../components/UserAvatar';

/** "@전체" sentinel — mention 노드 attrs.id 값으로 저장된다 */
export const MENTION_ALL_ID = '@all';

// ── Types ──
export interface MentionUserItem {
  kind: 'user';
  id: string;
  name: string;
  profileUrl?: string | null;
}

export interface MentionPageItem {
  kind: 'page';
  id: string;
  title: string;
}

export type UnifiedMentionItem = MentionUserItem | MentionPageItem;

interface CommandPayload {
  id: string;
  label: string;
  mentionType: 'user' | 'wiki-page';
}

// ── Suggestion List Component ──
interface UnifiedMentionListProps {
  items: UnifiedMentionItem[];
  command: (payload: CommandPayload) => void;
}

export interface UnifiedMentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const UnifiedMentionList = forwardRef<UnifiedMentionListRef, UnifiedMentionListProps>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => setSelectedIndex(0), [items]);

    useEffect(() => {
      // selectedIndex 위치의 버튼 스크롤 동기화
      const buttons = listRef.current?.querySelectorAll('button[data-mention-item]');
      const el = buttons?.[selectedIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    }, [selectedIndex]);

    const selectItem = useCallback(
      (index: number) => {
        const item = items[index];
        if (!item) return;
        if (item.kind === 'user') {
          command({ id: item.id, label: item.name, mentionType: 'user' });
        } else {
          command({ id: item.id, label: item.title, mentionType: 'wiki-page' });
        }
      },
      [items, command],
    );

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          setSelectedIndex((i) => (i + items.length - 1) % items.length);
          return true;
        }
        if (event.key === 'ArrowDown') {
          setSelectedIndex((i) => (i + 1) % items.length);
          return true;
        }
        if (event.key === 'Enter') {
          selectItem(selectedIndex);
          return true;
        }
        return false;
      },
    }));

    if (items.length === 0) {
      return (
        <div className="bg-popover border border-border rounded-lg shadow-lg p-3 text-xs text-muted-foreground">
          <Trans>No matching members or pages</Trans>
        </div>
      );
    }

    const userItems = items.filter((it): it is MentionUserItem => it.kind === 'user');
    const pageItems = items.filter((it): it is MentionPageItem => it.kind === 'page');

    // 전역 index → items 배열 index 매핑 (그룹별 렌더링 시 선택 인덱스 정렬용)
    const sectionItems: { item: UnifiedMentionItem; globalIndex: number }[] = [];
    userItems.forEach(u => sectionItems.push({ item: u, globalIndex: items.indexOf(u) }));
    pageItems.forEach(p => sectionItems.push({ item: p, globalIndex: items.indexOf(p) }));

    return (
      <div
        ref={listRef}
        className="bg-popover border border-border rounded-lg shadow-lg overflow-hidden max-h-64 overflow-y-auto min-w-[240px]"
      >
        {userItems.length > 0 && (
          <>
            <div className="px-3 py-1 text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30">
              <Trans>Members</Trans>
            </div>
            {userItems.map(u => {
              const globalIndex = items.indexOf(u);
              const isAll = u.id === MENTION_ALL_ID;
              return (
                <button
                  key={`u-${u.id}`}
                  data-mention-item
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors cursor-pointer',
                    globalIndex === selectedIndex
                      ? 'bg-accent text-accent-foreground'
                      : 'text-foreground hover:bg-muted',
                  )}
                  onClick={() => selectItem(globalIndex)}
                >
                  {isAll ? (
                    <div className="size-6 rounded-full bg-badge-caution/15 flex items-center justify-center shrink-0">
                      <Users className="size-3.5 text-badge-caution" />
                    </div>
                  ) : (
                    <UserAvatar src={u.profileUrl ?? null} name={u.name} className="size-6" />
                  )}
                  <span className="truncate">{u.name}</span>
                  {isAll && (
                    <span className="ml-auto text-meta-sm text-muted-foreground shrink-0"><Trans>Everyone</Trans></span>
                  )}
                </button>
              );
            })}
          </>
        )}

        {pageItems.length > 0 && (
          <>
            {userItems.length > 0 && <div className="border-t border-border" />}
            <div className="px-3 py-1 text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider bg-muted/30">
              <Trans>Wiki pages</Trans>
            </div>
            {pageItems.map(p => {
              const globalIndex = items.indexOf(p);
              return (
                <button
                  key={`p-${p.id}`}
                  data-mention-item
                  className={cn(
                    'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors cursor-pointer',
                    globalIndex === selectedIndex
                      ? 'bg-accent text-accent-foreground'
                      : 'text-foreground hover:bg-muted',
                  )}
                  onClick={() => selectItem(globalIndex)}
                >
                  <div className="size-6 rounded-md bg-muted flex items-center justify-center shrink-0">
                    <FileText className="size-3.5 text-muted-foreground" />
                  </div>
                  <span className="truncate">{p.title}</span>
                </button>
              );
            })}
          </>
        )}
      </div>
    );
  },
);

UnifiedMentionList.displayName = 'UnifiedMentionList';

// ── Suggestion Configuration Factory ──
/**
 * 통합 멘션 suggestion 옵션 생성.
 *
 * @param getMembers - 현재 채널/워크스페이스 멤버 목록
 * @param getPages   - 현재 게시판의 위키 페이지 목록 (위키 게시판이 아니면 빈 배열 반환)
 */
export function createUnifiedMentionSuggestion(
  getMembers: () => MentionUserItem[],
  getPages: () => MentionPageItem[],
  /** @all 멘션 권한 — 권한이 있으면 멤버 목록 최상단에 "전체" 항목 노출 */
  getCanMentionAll?: () => boolean,
): Omit<SuggestionOptions<UnifiedMentionItem>, 'editor'> {
  return {
    items: ({ query }: { query: string }) => {
      const members = getMembers();
      const pages = getPages();
      const lower = query.toLowerCase();

      // "@전체" 항목 — 권한 있고, 쿼리가 비었거나 '전체'/'all'로 시작할 때 노출
      const allName = t`Everyone`;
      const allItem: MentionUserItem | null = getCanMentionAll?.()
        ? { kind: 'user', id: MENTION_ALL_ID, name: allName, profileUrl: null }
        : null;
      const includeAll = allItem && (
        !query
        || '전체'.startsWith(query)
        || allName.toLowerCase().startsWith(lower)
        || 'all'.includes(lower)
      );

      const filteredMembers = !query
        ? members.slice(0, 8)
        : members.filter(m => m.name.toLowerCase().includes(lower)).slice(0, 8);

      const filteredPages = !query
        ? pages.slice(0, 8)
        : pages.filter(p => p.title.toLowerCase().includes(lower)).slice(0, 8);

      // "@전체" 최상단, 사용자, 페이지 순
      return [
        ...(includeAll && allItem ? [allItem] : []),
        ...filteredMembers,
        ...filteredPages,
      ];
    },

    render: () => {
      let component: ReactRenderer<UnifiedMentionListRef> | null = null;
      let popup: TippyInstance[] | null = null;

      return {
        onStart: (props: SuggestionProps<UnifiedMentionItem>) => {
          component = new ReactRenderer(UnifiedMentionList, {
            props,
            editor: props.editor,
          });

          if (!props.clientRect) return;

          popup = tippy('body', {
            getReferenceClientRect: props.clientRect as () => DOMRect,
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            trigger: 'manual',
            placement: 'bottom-start',
          });
        },

        onUpdate: (props: SuggestionProps<UnifiedMentionItem>) => {
          component?.updateProps(props);
          if (!props.clientRect) return;
          popup?.[0]?.setProps({
            getReferenceClientRect: props.clientRect as () => DOMRect,
          });
        },

        onKeyDown: (props: { event: KeyboardEvent }) => {
          if (props.event.key === 'Escape') {
            popup?.[0]?.hide();
            return true;
          }
          return component?.ref?.onKeyDown(props) ?? false;
        },

        onExit: () => {
          popup?.[0]?.destroy();
          component?.destroy();
        },
      };
    },
  };
}
